import React, { useEffect, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import {
  CustomSelect,
  ListItem,
} from '../../../../../components/Toolkit/Inputs/CustomSelect';
import { Button } from '../../../../../components/Toolkit/Button/Button';
import { Dropdown } from '../../../../../components/Toolkit/Dropdown/Dropdown';
import { ListMultiSelect } from '../../../../../components/DealerPage/components/Filters/ListMultiSelect/ListMultiSelect';
import { ListWrapper } from '../../../../../features/filters/components/BottomSheetList/BottomSheetList.styled';
import { searchPageApi } from '../../../../../api/searchPageApi';
import { IGetSearchFilterMakeModel } from '../../../../../types';
import { CloseOutlineIc } from '@dsch/dd-icons';
import { InputButton } from '../../../../../components/Toolkit/InputButton/InputButton';
import { BottomSheet } from '../../../../../components/Toolkit/BottomSheet/BottomSheet';
import { media } from '../../../../../helpers/breakpoints';

const SButton = styled(Button)`
  margin-bottom: ${({ theme }) => theme.spacing.S12};
  font-weight: normal;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  background-color: #ffffff;
  padding: 12px 16px;
  box-shadow: none;
  color: #333333;

  &:hover {
    border: 1px solid #333333;
    background-image: none;
    color: #333333;
    box-shadow: none;
    cursor: auto;
  }

  &:active {
    background-image: none;
    box-shadow: none;
    color: #333333;
  }
`;

const DeleteButton = styled.div`
  cursor: pointer;
  svg {
    color: ${({ theme }) => theme.colors.BLUE};
    margin-top: -3px;
  }
`;

const STitleContainer = styled.div`
  display: flex;
  width: 100%;
  .title-ellipsis {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .title-number {
    padding: ${({ theme }) => `0 ${theme.spacing.M24} 0 ${theme.spacing.S2}`};
  }
  ${DeleteButton} {
    position: absolute;
    right: 15px;
  }
`;

type MakeModelOptions = ListItem[];
export interface MakeModelGroupPropsType {
  id: string;
  section: string;
  makeValue: string;
  makeOptions: MakeModelOptions;
  onMakeChange: (id: string, value: string) => void;
  modelValue: string[];
  modelOptions: MakeModelOptions;
  onRemoveModel: (id: string, value: string) => void;
  onAddModel: (id: string, value: string) => void;
  isEditable: boolean;
  onRemove: (id: string) => void;
  onClear: (id: string) => void;
  dealerId?: string;
}

export const mapModelToOptions = (makeModels: MakeModelOptions) =>
  makeModels
    .map((item: ListItem) => ({
      value: item.value ?? '',
      label: item.displayName ?? '',
    }))
    .filter((item) => item.label !== 'Any');

const SMakeModelGroupSelect = styled.div`
  ${ListWrapper} {
    margin-top: ${({ theme }) => theme.spacing.M16};
  }
`;

export const mapModelOptions = (modelData: IGetSearchFilterMakeModel) => {
  const modelsAll = modelData.all ? mapModelToOptions(modelData.all) : [];
  const modelsPopular = modelData.popular
    ? mapModelToOptions(modelData.popular)
    : [];
  return modelsPopular.length > 0
    ? [
        { label: 'All Models', value: '' },
        { label: 'Popular', value: '' },
        ...modelsPopular,
        { label: 'Models list', value: '' },
        ...modelsAll,
      ]
    : [
        { label: 'All Models', value: '' },
        { label: 'Models list', value: '' },
        ...modelsAll,
      ];
};

const Mobile = styled.div<{ isDisabled?: boolean }>`
  display: block;
  ${media.large} {
    display: none;
  }
`;

const Desktop = styled.div<{ isDisabled?: boolean }>`
  display: none;
  ${media.large} {
    display: block;
  }
`;

const MakeModelGroupGlobalStyle = createGlobalStyle`
  .with-model .bottom-sheet-header-text {
    color: ${({ theme }) => theme.colors.GREY_DARKER};
  }
`;

const TextContainer = styled.span<{ textContainerMaxWidth: string }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;

  ${media.large} {
    max-width: ${({ textContainerMaxWidth }) => textContainerMaxWidth};
  }
`;

const MakeModelGroupSelect = ({
  id,
  section,
  makeValue,
  makeOptions,
  onMakeChange,
  modelValue,
  modelOptions,
  onRemoveModel,
  onAddModel,
  onClear,
  isEditable,
  onRemove,
  dealerId,
}: MakeModelGroupPropsType) => {
  const [makeExpanded, setMakeExpanded] = useState(false);

  const [openBottomSheet, setOpenBottomSheet] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState(false);

  const [modelDataOptions, setModelDataOptions] = useState<
    { value: string; label: string }[]
  >(mapModelToOptions(modelOptions));

  const newSelectedModelOptions =
    modelValue &&
    modelValue.length > 0 &&
    modelDataOptions.filter((modelDataItem) =>
      modelValue.includes(modelDataItem.label),
    );

  const textLabelModel = (withMake: boolean = false) => {
    if (modelValue && modelValue.length === 1) {
      return withMake ? `${makeValue} ${modelValue[0]}` : modelValue[0];
    } else if (modelValue && modelValue.length > 1) {
      const modelCountText = `(+${modelValue.length - 1})`;
      return withMake
        ? `${makeValue} ${modelValue[0]} ${modelCountText}`
        : `${modelValue[0]} ${modelCountText}`;
    } else {
      return withMake ? `${makeValue} (All Models)` : 'All Models';
    }
  };

  const isModelEnabled =
    !!makeValue &&
    makeValue !== 'All Makes' &&
    modelDataOptions &&
    modelDataOptions.length > 0;

  const fetchModelByMake = async (make: string) => {
    setIsLoading(true);
    try {
      const models = await searchPageApi.getModelByMake(
        section,
        make,
        { accept: 'application/json' },
        dealerId,
      );
      setModelDataOptions(mapModelOptions(models.data));
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (makeValue === 'All Makes') {
      setModelDataOptions([]);
    } else {
      if (modelOptions.length > 0) {
        setModelDataOptions(mapModelToOptions(modelOptions));
      } else {
        makeValue && isEditable && fetchModelByMake(makeValue);
      }
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeValue]);

  useEffect(() => {
    if (newSelectedModelOptions && modelValue.includes('All Models')) {
      setModelDataOptions([]);
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelValue]);

  const isDisabled = !isLoading && !isModelEnabled;

  const getFilterItemSelected = () => {
    const itemText = textLabelModel(true);
    const items = itemText && itemText.split(' ');
    const hasModelsNumbers = items[items.length - 1].includes('(+');

    const removeButton = (
      <DeleteButton onClick={() => onRemove(id)}>
        <CloseOutlineIc width="1em" height="1em" />
      </DeleteButton>
    );

    if (hasModelsNumbers) {
      return (
        <SButton ofType="SECONDARY">
          <STitleContainer>
            <span className="title-ellipsis">
              {itemText.replace(items[items.length - 1], '')}
            </span>
            <span className="title-number">{items[items.length - 1]}</span>
            {removeButton}
          </STitleContainer>
        </SButton>
      );
    } else {
      return (
        <SButton ofType="SECONDARY">
          <TextContainer textContainerMaxWidth="228px">
            {textLabelModel(true)}
          </TextContainer>
          {removeButton}
        </SButton>
      );
    }
  };

  const isNotAllModels = textLabelModel() !== 'All Models';

  return (
    <SMakeModelGroupSelect data-testid="make-model-dropdowns" key={id}>
      {isEditable ? (
        <>
          <Mobile data-testid="mobile-make-dropdown" isDisabled={isDisabled}>
            {isNotAllModels && <MakeModelGroupGlobalStyle />}
            <InputButton
              isActive={openBottomSheet}
              value={makeValue ? makeValue : makeOptions[0].displayName}
              onClick={() => !isLoading && setOpenBottomSheet(true)}
              placeholder={'Make'}
              isLoading={isLoading}
            />
            <BottomSheet
              isOpen={openBottomSheet}
              askToClose={() => setOpenBottomSheet(false)}
              headerText={'Make'}
              onApply={() => setOpenBottomSheet(false)}
            >
              <CustomSelect
                listData={makeOptions}
                onSelect={({
                  displayName,
                }: {
                  value: string;
                  displayName: string;
                }) => {
                  onMakeChange && onMakeChange(id, displayName);
                  setOpenBottomSheet(false);
                }}
              />
            </BottomSheet>
          </Mobile>
          <Desktop isDisabled={isDisabled}>
            <Dropdown
              data-testid="desktop-make-dropdown"
              expanded={makeExpanded}
              setExpanded={setMakeExpanded}
              displayedValue={
                makeValue ? makeValue : makeOptions[0].displayName
              }
              placeholder={'Make'}
            >
              <CustomSelect
                listData={makeOptions}
                onSelect={({
                  displayName,
                }: {
                  value: string;
                  displayName: string;
                }) => {
                  onMakeChange && onMakeChange(id, displayName);
                  setMakeExpanded(false);
                }}
              />
            </Dropdown>
          </Desktop>
          <ListMultiSelect
            name={id}
            options={modelDataOptions}
            selectedOptions={
              newSelectedModelOptions && newSelectedModelOptions.length > 0
                ? newSelectedModelOptions
                : [modelDataOptions[0]]
            }
            placeholder={textLabelModel()}
            onSelectOption={(item, isChecked) => {
              if (isChecked) {
                return onRemoveModel(id, item.value);
              }
              onAddModel(id, item.value);
            }}
            onClear={() => onClear(id)}
            isLoading={isLoading}
            isDisabled={isDisabled}
            bottomSheetClassName={
              isNotAllModels ? 'with-model' : 'with-no-model'
            }
          />
        </>
      ) : (
        getFilterItemSelected()
      )}
    </SMakeModelGroupSelect>
  );
};

export { MakeModelGroupSelect };
